import { Link } from 'react-router';
import {
  BUTTON_CLASS,
  DataTable,
  formatUtcDate,
  MetaRow,
  neutralButtonStyle,
  StatusDot,
  token,
  type DataTableColumn,
} from '@bp-files/ui';
import { CARD_BODY_CLASS, CardGrid, NavCard } from '@/components/CardGrid';
import { FoldersIcon } from '@/components/FoldersIcon';
import { formatBytes } from '@/lib/formatBytes';
import { useFolderDestinations, type DisplayFolderDestination } from '@/lib/useFolderDestinations';
import { useViewMode } from '@/lib/useViewMode';
import { FOLDER_DESTINATIONS_COPY } from './folderDestinations.copy';

/**
 * The Files surface's body — one ungrouped list of every destination the caller
 * can open (SPEC §6, FIL-1d, FIL-1e).
 *
 * **One surface, both roles.** An admin used to get a shared-space card grid
 * here and a navigable space root beneath it, on the reasoning that maintaining
 * structure means seeing the container (ADR-0088). The container is not a place
 * any more: a space's root holds files directly, so it is a destination in this
 * same list.
 *
 * **Creating a folder moved back onto a navigated-into place.** ADR-0088 put it
 * here instead, asking which space rather than making the admin stand in one
 * first — the container was gone, so there was nowhere left to stand. A space's
 * root is a destination in this list now (ADR-0090), which gives folder
 * creation a room to stand in again: it lives in `FolderView`'s own overflow,
 * beside Notifications and retention, and the space is implicit from the route
 * rather than picked from a dropdown. This surface goes back to being exactly
 * what its own name says — a list, and nothing that creates anything.
 *
 * **A destination is a folder or a space's root**, and the difference is
 * `isRoot` rather than anything visible: a root is labelled with its space's
 * name, a folder with `Space — Folder`. `useFolderDestinations` owns that rule
 * and the alphabetical order, which puts each space's root immediately above its
 * own folders.
 */
export function FolderDestinations({ isAdmin = false }: { isAdmin?: boolean }) {
  const { state, retry } = useFolderDestinations();
  const mode = useViewMode();

  if (state.kind === 'loading') {
    return (
      <p role="status" className="text-sm" style={{ color: token('bp-text-secondary') }}>
        {FOLDER_DESTINATIONS_COPY.loading}
      </p>
    );
  }

  if (state.kind === 'error') {
    const refused = state.status === 403;
    return (
      <div className="flex flex-col items-start gap-3">
        <p role="alert" className="flex items-center gap-2 text-sm text-danger">
          <StatusDot tone="alert" />
          {refused ? FOLDER_DESTINATIONS_COPY.forbidden : FOLDER_DESTINATIONS_COPY.error}
        </p>
        {refused ? null : (
          <button
            type="button"
            onClick={retry}
            className={BUTTON_CLASS}
            style={neutralButtonStyle()}
          >
            {FOLDER_DESTINATIONS_COPY.retry}
          </button>
        )}
      </div>
    );
  }

  /*
    Every space contributes a root, so an empty list means no `Active`
    membership at all — the "spaces exist but none is open" state the grid
    needed a third sentence for cannot arise here.
  */
  if (state.folders.length === 0) return <EmptyDestinations isAdmin={isAdmin} />;

  return <FolderDestinationList folders={state.folders} isDesktop={mode === 'table'} />;
}

/** File count and size, shared by the destination table and cards. */
function destinationDetail(folder: DisplayFolderDestination): string {
  return folder.fileCount === 0
    ? FOLDER_DESTINATIONS_COPY.details.empty
    : FOLDER_DESTINATIONS_COPY.details.countAndSize(
        folder.fileCount,
        formatBytes(folder.totalBytes, FOLDER_DESTINATIONS_COPY.units),
      );
}

function folderPath(folder: DisplayFolderDestination): string {
  return `/files/spaces/${encodeURIComponent(folder.spaceId)}/folders/${encodeURIComponent(folder.folderId)}`;
}

/** The ungrouped destination list, rendered as cards or a table. */
export function FolderDestinationList({
  folders,
  isDesktop,
}: {
  folders: DisplayFolderDestination[];
  isDesktop: boolean;
}) {
  const columns: DataTableColumn<DisplayFolderDestination>[] = [
    {
      id: 'space',
      header: FOLDER_DESTINATIONS_COPY.columns.folder,
      sortable: true,
      sortValue: (folder) => folder.displayName,
      cell: (folder) => (
        <Link
          to={folderPath(folder)}
          className="flex items-center gap-2 font-medium hover:underline"
          style={{ color: token('bp-text') }}
        >
          <FoldersIcon className="shrink-0 text-secondary" />
          {folder.displayName}
        </Link>
      ),
    },
    {
      // Enough detail to choose a destination without opening it first.
      id: 'details',
      header: FOLDER_DESTINATIONS_COPY.columns.details,
      cell: (folder) => (
        <span style={{ color: token('bp-text-secondary') }}>{destinationDetail(folder)}</span>
      ),
    },
    {
      id: 'lastActivity',
      header: FOLDER_DESTINATIONS_COPY.columns.lastActivity,
      sortable: true,
      sortValue: (folder) => folder.lastActivityAt,
      // Just the date, not the full "Last activity on…" sentence — the column
      // header already carries it, `FolderTable`'s own rule for the same pair.
      cell: (folder) => (
        <MetaRow
          time={formatUtcDate(folder.lastActivityAt, 'en-US')}
          dateTime={folder.lastActivityAt}
        />
      ),
    },
  ];

  return (
    <section className="flex flex-col gap-3">
      {isDesktop ? (
        <DataTable
          columns={columns}
          rows={folders}
          rowKey={(folder) => `${folder.spaceId}/${folder.folderId}`}
          caption={FOLDER_DESTINATIONS_COPY.tableCaption}
          defaultSort={{ id: 'space', direction: 'asc' }}
        />
      ) : (
        <CardGrid label={FOLDER_DESTINATIONS_COPY.tableCaption}>
          {folders.map((folder) => (
            <li key={`${folder.spaceId}/${folder.folderId}`}>
              {/* Counts and sizes use the same caller-visible tallies as the folder itself. */}
              <NavCard
                to={folderPath(folder)}
                icon={<FoldersIcon />}
                name={folder.displayName}
                detail={
                  <span className="flex flex-col gap-1">
                    <span className={CARD_BODY_CLASS} style={{ color: token('bp-text-secondary') }}>
                      {destinationDetail(folder)}
                    </span>
                  </span>
                }
                meta={
                  <MetaRow
                    time={FOLDER_DESTINATIONS_COPY.details.lastActivity(
                      formatUtcDate(folder.lastActivityAt, 'en-US'),
                    )}
                    dateTime={folder.lastActivityAt}
                    size="sm"
                  />
                }
              />
            </li>
          ))}
        </CardGrid>
      )}
    </section>
  );
}

/**
 * Nothing to open, said two ways.
 *
 * **The admin sentence is what keeps this surface from being a dead end.**
 * Creating a space lives on Spaces, so a brand-new agency's first admin opens
 * Files, finds nothing, and would otherwise have no control in front of them at
 * all. That, and nothing else, is why this component takes `isAdmin`.
 */
function EmptyDestinations({ isAdmin }: { isAdmin: boolean }) {
  return (
    <div className="flex flex-col items-start gap-3">
      <p className="text-sm" style={{ color: token('bp-text-secondary') }}>
        {isAdmin
          ? FOLDER_DESTINATIONS_COPY.states.emptyAdmin
          : FOLDER_DESTINATIONS_COPY.states.empty}
      </p>

      {isAdmin ? (
        <Link to="/spaces" className={BUTTON_CLASS} style={neutralButtonStyle()}>
          {FOLDER_DESTINATIONS_COPY.states.manage}
        </Link>
      ) : null}
    </div>
  );
}
